import React, { createContext, useEffect, useReducer, useState } from 'react';
import { IStoreState, IFilterState } from 'src/@noiseEvents/props';
import { dataInitialState, dataReducer } from 'src/@noiseEvents/reducers/dataReducer';
import { sortInitialState, sortReducer } from 'src/@noiseEvents/reducers/sortReducer';
import { filterInitialState, filterReducer } from 'src/@noiseEvents/reducers/filterReducer';
// Hooks
import { usePersistentReducer } from 'src/utils';
import { serializeFiltersToUrl, useParseUrlFilters } from 'src/utils/filters/parseUrlFilters';
import {
  filterKeyMap,
  tableFiltersToNoiseEventFilter,
  urlToNoiseEventFilterState,
} from '../reducers/constants';
import { NoiseEventSummaryFilter } from 'src/app/gql/__generated__/hooks';
import { useHistory, useLocation } from 'react-router-dom';
//import { transformToKeys } from 'src/@operations/providers/utils';

export const NoiseEventsStateContext = createContext({
  data: dataInitialState,
  sort: sortInitialState,
  filter: filterInitialState,
});

export const NoiseEventsDispatchContext = createContext(null);

export const NoiseEventsStateProvider = ({ children }) => {
  const [dataState, dataDispatch] = useReducer(dataReducer, dataInitialState);
  const [sortState, sortDispatch] = useReducer(sortReducer, sortInitialState);

  const urlFilterState = useParseUrlFilters<NoiseEventSummaryFilter>(filterKeyMap);

  const storageMechanism = Object.keys(urlFilterState).length > 0 ? 'url' : 'session';

  const urlState = urlToNoiseEventFilterState(urlFilterState);

  const combinedFilterState: IFilterState = {
    ...filterInitialState,
    isLoadedFromUrl: !!Object.keys(urlFilterState).length,
    tableFilters: {
      ...filterInitialState.tableFilters,
      ...urlState.tableFilters,
    },
    time: urlState.time,
    sel: urlState.sel,
    maxLevel: urlState.maxLevel,
    duration: urlState.duration,
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [filterState, filterDispatch] = usePersistentReducer(
    filterReducer,
    combinedFilterState,
    'noise-events.filter.state',
    storageMechanism
  );

  const state: IStoreState = {
    data: dataState,
    sort: sortState,
    filter: filterState,
  };

  const dispatchers = [dataDispatch, sortDispatch, filterDispatch];

  const dispatch: any = (action: any) =>
    dispatchers.map((dispatcher: any) => {
      dispatcher(action);
    });

  const location = useLocation();
  const history = useHistory();
  const [defaultLoad, setDefaultLoad] = useState(true);

  useEffect(() => {
    //const filterKeys = transformToKeys(state.filter.tableFilters);

    if (defaultLoad) {
      setDefaultLoad(false);
      return;
    } else {
      if (defaultLoad) {
        setDefaultLoad(false);
      }
    }

    // Create a clean filter object excluding internal fields
    const filtersForUrl = { ...state.filter.tableFilters };
    const timeRangeForUrl = state.filter.time;
    /* const selRangeForUrl = state.filter.sel;
    const maxLevelRangeForUrl = state.filter.maxLevel;
    const durationRangeForUrl = state.filter.duration; */

    const { filter } = tableFiltersToNoiseEventFilter(filtersForUrl, timeRangeForUrl);

    const urlSearchParams = serializeFiltersToUrl(
      {
        ...filter,
        ...(state.filter.sel && { sel: state.filter.sel }),
        ...(state.filter.maxLevel && { maxLevel: state.filter.maxLevel }),
        ...(state.filter.duration && { duration: state.filter.duration }),
      },
      filterKeyMap,
      true
    );

    const currentSearch = location.search.substring(1); // Remove leading '?'
    // Only update URL if the search params have actually changed
    if (urlSearchParams !== currentSearch) {
      const newPath = `${location.pathname}${urlSearchParams ? `?${urlSearchParams}` : ''}`;
      history.replace(newPath, location.state);
    }
  }, [state.filter, location.pathname, location.search, location.state, history]);

  return (
    <NoiseEventsStateContext.Provider value={state}>
      <NoiseEventsDispatchContext.Provider value={dispatch}>
        {children}
      </NoiseEventsDispatchContext.Provider>
    </NoiseEventsStateContext.Provider>
  );
};
